import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MobileMenuTrigger } from '@/components/Layout';
import { PerformanceSkeleton } from '@/components/Skeleton';
import { hapticSuccess } from '@/utils/haptics';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Target, DollarSign, 
  CheckCircle, Clock, XCircle, RefreshCw, BarChart3,
  ArrowDownRight, Minus, Phone, Calendar, Flame, 
  AlertTriangle, ChevronRight, MapPin, Zap, Award, Info,
  Building2, Filter, FileText, CalendarDays, X, Edit3
} from 'lucide-react';
import { toast } from 'sonner';

// Polling interval for live updates (45 seconds)
const POLLING_INTERVAL = 45000;

// Time range options
const TIME_RANGES = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'qtd', label: 'Quarter to Date' },
  { value: 'fytd', label: 'Financial Year' },
  { value: 'custom', label: 'Custom Range' }
];

// Status filter options
const STATUS_FILTERS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'agreement_in_progress', label: 'Agreement In Progress' },
  { value: 'agreement_done', label: 'Agreement Done' }
];

// Format currency in Lakhs/Crores
const formatCurrency = (lacs) => {
  if (!lacs || lacs === 0) return '₹0';
  if (lacs >= 100) return `₹${(lacs / 100).toFixed(2)}Cr`;
  return `₹${lacs.toFixed(1)}L`;
};

// Format INR
const formatINR = (value) => {
  if (!value || value === 0) return '₹0';
  return `₹${value.toLocaleString('en-IN')}`;
};

// Slab Progress Meter Component
const SlabProgressMeter = ({ currentAVCr, slabs }) => {
  const milestones = [0, 1, 1.5, 2, 2.5, 3, 3.5, 4];
  const maxValue = Math.max(4, currentAVCr + 0.5);
  
  // Calculate position percentage
  const getPosition = (value) => {
    return Math.min((value / maxValue) * 100, 100);
  };
  
  // Find current slab index for highlighting
  const getCurrentSlabIndex = () => {
    if (currentAVCr >= 3) return 5;
    if (currentAVCr >= 2.5) return 4;
    if (currentAVCr >= 2) return 3;
    if (currentAVCr >= 1.5) return 2;
    if (currentAVCr >= 1) return 1;
    return 0;
  };
  
  const currentSlabIdx = getCurrentSlabIndex();
  
  return (
    <div className="relative pt-6 pb-2">
      {/* Progress Track */}
      <div className="relative h-3 bg-slate-200 rounded-full overflow-hidden">
        {/* Filled Progress */}
        <div 
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full transition-all duration-700"
          style={{ width: `${getPosition(currentAVCr)}%` }}
        />
        
        {/* Slab dividers */}
        {milestones.map((val, idx) => (
          <div 
            key={idx}
            className="absolute top-0 h-full w-0.5 bg-white/50"
            style={{ left: `${getPosition(val)}%` }}
          />
        ))}
      </div>
      
      {/* Current Position Indicator */}
      <div 
        className="absolute -top-1 transform -translate-x-1/2 transition-all duration-700"
        style={{ left: `${getPosition(currentAVCr)}%` }}
      >
        <div className="w-5 h-5 bg-purple-600 border-2 border-white shadow-lg rounded-full flex items-center justify-center">
          <Zap size={10} className="text-white" />
        </div>
      </div>
      
      {/* Labels */}
      <div className="flex justify-between mt-3 text-xs">
        {milestones.slice(0, 6).map((val, idx) => (
          <div 
            key={idx} 
            className={`text-center ${idx <= currentSlabIdx ? 'text-purple-600 font-semibold' : 'text-slate-400'}`}
            style={{ width: '16.66%' }}
          >
            {val === 0 ? '₹0' : `${val}Cr`}
          </div>
        ))}
      </div>
    </div>
  );
};

// Enhanced Booking Contribution Table with Time Range and Live Updates
const BookingContributionTable = ({ 
  onRefreshRequest,
  pollingEnabled = true 
}) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('this_month');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  const [lastFetched, setLastFetched] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const pollingRef = useRef(null);
  
  // Edit Request State
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({
    booking_date: '',
    unit_no: '',
    agreement_value_lacs: '',
    token_amount_lacs: '',
    reason: ''
  });
  const [submittingEdit, setSubmittingEdit] = useState(false);

  // Edit & Resubmit Modal State (for QUERY_RAISED bookings)
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [resubmitBooking, setResubmitBooking] = useState(null);
  const [resubmitForm, setResubmitForm] = useState({
    unit_no: '',
    agreement_value_lacs: '',
    token_amount_lacs: '',
    correction_notes: ''
  });
  const [submittingResubmit, setSubmittingResubmit] = useState(false);
  
  // Document upload state - supports multiple document types
  const DOCUMENT_TYPES = [
    { key: 'COST_SHEET', label: 'Cost Sheet' },
    { key: 'BOOKING_FORM', label: 'Booking Form' },
    { key: 'PAYMENT_PROOF', label: 'Payment Proof' },
    { key: 'CUSTOMER_KYC', label: 'Customer KYC' },
    { key: 'TOKEN_RECEIPT', label: 'Token Receipt' },
    { key: 'OTHER', label: 'Other Document' }
  ];
  const [bookingDocuments, setBookingDocuments] = useState({});
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [showAddDocDropdown, setShowAddDocDropdown] = useState(false);
  
  // Edit Lock state - prevents simultaneous edits
  const [editLockInfo, setEditLockInfo] = useState(null);
  const [acquiringLock, setAcquiringLock] = useState(false);

  // Fetch bookings with current filters
  const fetchBookings = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {
        range: timeRange,
        status: statusFilter === 'all' ? null : statusFilter
      };
      
      if (timeRange === 'custom' && customStart && customEnd) {
        params.start = customStart;
        params.end = customEnd;
      }
      
      const response = await api.getMyBookingsEnhanced(params);
      const data = response.data;
      
      setBookings(data.bookings || []);
      setStatusCounts(data.status_counts || {});
      setDateRange({ start: data.start_date, end: data.end_date });
      setLastFetched(new Date());
    } catch (error) {
      if (!silent) toast.error('Failed to load bookings');
      console.error('Fetch bookings error:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange, statusFilter, customStart, customEnd]);

  // Initial fetch and filter changes
  useEffect(() => {
    if (timeRange !== 'custom' || (customStart && customEnd)) {
      fetchBookings();
    }
  }, [timeRange, statusFilter, customStart, customEnd, fetchBookings]);

  // Setup polling for live updates
  useEffect(() => {
    if (pollingEnabled) {
      pollingRef.current = setInterval(() => {
        fetchBookings(true); // Silent refresh
      }, POLLING_INTERVAL);
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [pollingEnabled, fetchBookings]);

  // Handle time range change
  const handleTimeRangeChange = (value) => {
    setTimeRange(value);
    if (value === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
    }
  };

  // Apply custom date range
  const applyCustomRange = () => {
    if (customStart && customEnd) {
      if (new Date(customStart) > new Date(customEnd)) {
        toast.error('Start date must be before end date');
        return;
      }
      setShowCustomDatePicker(false);
      fetchBookings();
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  // Open edit dialog for a booking
  const handleEditBooking = (booking) => {
    setEditBooking(booking);
    setEditForm({
      booking_date: booking.booking_date || '',
      unit_no: booking.unit_no || '',
      agreement_value_lacs: booking.av_lacs?.toString() || '',
      token_amount_lacs: booking.token_lacs?.toString() || '',
      reason: ''
    });
    setShowEditDialog(true);
  };

  // Submit edit request
  const handleSubmitEditRequest = async () => {
    if (!editForm.reason.trim()) {
      toast.error('Please provide a reason for the edit');
      return;
    }

    // Build requested changes (only include changed fields)
    const requestedChanges = {};
    if (editForm.booking_date !== editBooking.booking_date) {
      requestedChanges.booking_date = editForm.booking_date;
    }
    if (editForm.unit_no !== editBooking.unit_no) {
      requestedChanges.unit_no = editForm.unit_no;
    }
    if (parseFloat(editForm.agreement_value_lacs) !== editBooking.av_lacs) {
      requestedChanges.agreement_value_lacs = parseFloat(editForm.agreement_value_lacs);
    }
    if (parseFloat(editForm.token_amount_lacs) !== editBooking.token_lacs) {
      requestedChanges.token_amount_lacs = parseFloat(editForm.token_amount_lacs);
    }

    if (Object.keys(requestedChanges).length === 0) {
      toast.error('No changes detected');
      return;
    }

    setSubmittingEdit(true);
    try {
      await api.createBookingEditRequest({
        booking_id: editBooking.booking_id,
        requested_changes: requestedChanges,
        reason: editForm.reason.trim()
      });
      toast.success('Edit request submitted for manager approval');
      setShowEditDialog(false);
      setEditBooking(null);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit edit request';
      toast.error(msg);
    } finally {
      setSubmittingEdit(false);
    }
  };

  // Open Edit & Resubmit Modal for QUERY_RAISED bookings
  const openResubmitModal = async (booking) => {
    setAcquiringLock(true);
    setEditLockInfo(null);
    
    try {
      // Try to acquire edit lock first
      const lockResponse = await api.acquireBookingLock(booking.booking_id);
      const lockData = lockResponse.data;
      
      if (!lockData.success) {
        // Lock held by someone else
        setEditLockInfo({
          locked: true,
          lockedBy: lockData.locked_by_name,
          expiresIn: lockData.lock_expires_in_minutes,
          message: lockData.message
        });
        toast.error(lockData.message);
        setAcquiringLock(false);
        return;
      }
      
      // Lock acquired successfully - proceed to open modal
      setResubmitBooking(booking);
      setResubmitForm({
        unit_no: booking.unit_no || '',
        agreement_value_lacs: booking.av_lacs?.toString() || '',
        token_amount_lacs: booking.token_lacs?.toString() || '',
        correction_notes: ''
      });
      
      // Load existing documents from booking
      const existingDocs = {};
      if (booking.booking_documents && Array.isArray(booking.booking_documents)) {
        booking.booking_documents.forEach(doc => {
          existingDocs[doc.document_type] = {
            url: doc.file_url,
            uploaded_by_name: doc.uploaded_by_name,
            uploaded_at: doc.uploaded_at,
            isExisting: true
          };
        });
      }
      // Also check legacy fields
      if (booking.cost_sheet_url && !existingDocs['COST_SHEET']) {
        existingDocs['COST_SHEET'] = { url: booking.cost_sheet_url, isExisting: true };
      }
      if (booking.booking_form_url && !existingDocs['BOOKING_FORM']) {
        existingDocs['BOOKING_FORM'] = { url: booking.booking_form_url, isExisting: true };
      }
      if (booking.payment_proof_url && !existingDocs['PAYMENT_PROOF']) {
        existingDocs['PAYMENT_PROOF'] = { url: booking.payment_proof_url, isExisting: true };
      }
      setBookingDocuments(existingDocs);
      setShowAddDocDropdown(false);
      setShowResubmitModal(true);
      
    } catch (error) {
      console.error('Failed to acquire edit lock:', error);
      toast.error('Failed to open edit modal. Please try again.');
    } finally {
      setAcquiringLock(false);
    }
  };
  
  // Release edit lock when closing modal
  const closeResubmitModal = async () => {
    if (resubmitBooking) {
      try {
        await api.releaseBookingLock(resubmitBooking.booking_id);
      } catch (error) {
        console.error('Failed to release lock:', error);
      }
    }
    setShowResubmitModal(false);
    setResubmitBooking(null);
    setEditLockInfo(null);
  };

  // Handle document upload for any document type
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      return;
    }
    
    setUploadingDocType(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.uploadBookingDocument(formData, docType.toLowerCase());
      
      setBookingDocuments(prev => ({
        ...prev,
        [docType]: {
          url: response.data.url,
          uploaded_by_name: 'You',
          uploaded_at: new Date().toISOString(),
          isNew: true
        }
      }));
      toast.success(`${DOCUMENT_TYPES.find(d => d.key === docType)?.label || 'Document'} uploaded`);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDocType(null);
    }
  };

  // Remove document
  const handleRemoveDocument = (docType) => {
    setBookingDocuments(prev => {
      const updated = { ...prev };
      delete updated[docType];
      return updated;
    });
  };

  // Handle resubmit booking
  const handleResubmitBooking = async () => {
    if (!resubmitBooking) return;
    
    if (!resubmitForm.correction_notes.trim()) {
      toast.error('Please add correction notes explaining what was fixed');
      return;
    }
    
    // Check if at least one field was changed OR document uploaded
    const origUnit = resubmitBooking.unit_no || '';
    const origAV = resubmitBooking.av_lacs?.toString() || '';
    const origToken = resubmitBooking.token_lacs?.toString() || '';
    
    // Check for new documents
    const hasNewDocuments = Object.values(bookingDocuments).some(doc => doc.isNew);
    
    const hasChanges = (
      resubmitForm.unit_no !== origUnit ||
      resubmitForm.agreement_value_lacs !== origAV ||
      resubmitForm.token_amount_lacs !== origToken ||
      hasNewDocuments
    );
    
    if (!hasChanges) {
      toast.error('Please update at least one field or upload a document before submitting correction');
      return;
    }
    
    // Build documents array for API
    const documentsToSubmit = Object.entries(bookingDocuments)
      .filter(([_, doc]) => doc.isNew)
      .map(([docType, doc]) => ({
        document_type: docType,
        file_url: doc.url
      }));
    
    setSubmittingResubmit(true);
    try {
      await api.caResubmitBooking(resubmitBooking.booking_id, {
        correction_notes: resubmitForm.correction_notes.trim(),
        unit_number: resubmitForm.unit_no !== origUnit ? resubmitForm.unit_no : undefined,
        agreement_value_lacs: resubmitForm.agreement_value_lacs !== origAV ? parseFloat(resubmitForm.agreement_value_lacs) : undefined,
        token_amount_lacs: resubmitForm.token_amount_lacs !== origToken ? parseFloat(resubmitForm.token_amount_lacs) : undefined,
        booking_documents: documentsToSubmit.length > 0 ? documentsToSubmit : undefined
      });
      
      toast.success('Correction submitted successfully');
      hapticSuccess();
      // Lock is automatically released by backend after successful resubmit
      setShowResubmitModal(false);
      setResubmitBooking(null);
      setEditLockInfo(null);
      
      // Refresh bookings to show updated status
      fetchBookings(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'Validation error');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to submit correction');
      }
    } finally {
      setSubmittingResubmit(false);
    }
  };

  // Get status badge
  const getStatusBadge = (booking) => {
    const displayStatus = booking.display_status;
    const rawStatus = booking.status;
    
    // Check for Awaiting Re-Verification (resubmitted after query)
    if (rawStatus === 'SUBMITTED' && booking.resubmitted_at) {
      return <span className="px-2 py-0.5 bg-amber-500 text-white rounded-full text-xs font-medium flex items-center gap-1">🟡 Re-Verification</span>;
    }
    
    switch (displayStatus) {
      case 'Cancelled':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">Cancelled</span>;
      case 'Rejected':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">Rejected</span>;
      case 'Agreement Done':
        return <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">Agreement Done</span>;
      case 'Agreement In Progress':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Agreement In Progress</span>;
      case 'Approved':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Approved</span>;
      case 'Action Needed':
        return <span className="px-2 py-0.5 bg-red-500 text-white rounded-full text-xs font-medium flex items-center gap-1"><AlertTriangle size={10} />Action Needed</span>;
      case 'Pending Approval':
      default:
        return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Pending</span>;
    }
  };

  // Get incentive status
  const getIncentiveStatus = (booking) => {
    const status = booking.incentive_status;
    if (status === 'Confirmed') {
      return <span className="text-xs text-emerald-600 font-medium">✓ Confirmed</span>;
    }
    if (status === 'Notional') {
      return <span className="text-xs text-blue-600">Notional</span>;
    }
    return <span className="text-xs text-slate-400">—</span>;
  };

  // Format date range label
  const getDateRangeLabel = () => {
    if (!dateRange.start || !dateRange.end) return '';
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    return `${start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  // Calculate total AV
  const totalAV = useMemo(() => {
    return bookings.reduce((sum, b) => sum + (b.av_lacs || 0), 0);
  }, [bookings]);

  // Count action needed bookings
  // Calculate stats
  const stats = useMemo(() => {
    const actionNeeded = bookings.filter(b => b.action_required).length;
    const pendingVerification = bookings.filter(b => 
      !b.action_required && 
      (b.display_status === 'Pending Approval' || b.status === 'SUBMITTED' || b.status === 'CITY_HEAD_VERIFIED')
    ).length;
    const approved = bookings.filter(b => 
      ['Approved', 'Agreement Done', 'Agreement In Progress'].includes(b.display_status) ||
      ['CRM_VERIFIED', 'STRATEGY_VERIFIED', 'AGREEMENT_IN_PROGRESS', 'AGREEMENT_DONE', 'REGISTRATION_DONE', 'REVENUE_RECEIVED'].includes(b.status)
    ).length;
    return { actionNeeded, pendingVerification, approved };
  }, [bookings]);

  return (
    <Card className="p-4 bg-white rounded-2xl shadow-sm border-0">
      {/* Booking Stats Dashboard */}
      <div className="grid grid-cols-4 gap-2 mb-4 p-3 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl">
        {/* Action Required */}
        <div className={`p-2 rounded-lg text-center ${stats.actionNeeded > 0 ? 'bg-red-100' : 'bg-white/60'}`}>
          <p className={`text-lg font-bold ${stats.actionNeeded > 0 ? 'text-red-600' : 'text-slate-300'}`}>
            {stats.actionNeeded > 0 ? '⚠️' : '✓'} {stats.actionNeeded}
          </p>
          <p className="text-[10px] text-slate-500 font-medium">Action Needed</p>
        </div>
        
        {/* Pending Verification */}
        <div className="p-2 rounded-lg text-center bg-amber-50">
          <p className="text-lg font-bold text-amber-600">🟡 {stats.pendingVerification}</p>
          <p className="text-[10px] text-slate-500 font-medium">Pending</p>
        </div>
        
        {/* Approved */}
        <div className="p-2 rounded-lg text-center bg-emerald-50">
          <p className="text-lg font-bold text-emerald-600">🟢 {stats.approved}</p>
          <p className="text-[10px] text-slate-500 font-medium">Approved</p>
        </div>
        
        {/* Total AV */}
        <div className="p-2 rounded-lg text-center bg-purple-50">
          <p className="text-lg font-bold text-purple-600">💰</p>
          <p className="text-[10px] text-slate-500 font-medium">{formatCurrency(totalAV)}</p>
        </div>
      </div>

      {/* Action Needed Alert Banner */}
      {stats.actionNeeded > 0 && (
        <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2" data-testid="action-needed-alert">
          <div className="p-1.5 bg-red-100 rounded-lg">
            <AlertTriangle size={14} className="text-red-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-red-800">
              ⚠️ {stats.actionNeeded} Booking{stats.actionNeeded > 1 ? 's' : ''} Require{stats.actionNeeded === 1 ? 's' : ''} Immediate Action
            </p>
            <p className="text-[10px] text-red-600">Query raised by City Head - Click "Edit & Resubmit" below</p>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
            <FileText size={16} className="text-slate-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">My Bookings</h3>
            <p className="text-xs text-slate-400">{bookings.length} total • {formatCurrency(totalAV)} AV</p>
          </div>
        </div>
        <button 
          onClick={() => fetchBookings()} 
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw size={14} className={`text-slate-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap gap-2 mb-3">
        {/* Time Range Selector */}
        <Select value={timeRange} onValueChange={handleTimeRangeChange}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="time-range-selector">
            <CalendarDays size={12} className="mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIME_RANGES.map(r => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="status-filter">
            <Filter size={12} className="mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map(f => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
                {f.value !== 'all' && statusCounts[f.value] !== undefined && (
                  <span className="ml-1 text-slate-400">({statusCounts[f.value]})</span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Custom Date Range Picker */}
      {showCustomDatePicker && (
        <div className="mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1">
              <Label className="text-xs text-slate-500">From</Label>
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="h-8 text-xs"
                data-testid="custom-start-date"
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs text-slate-500">To</Label>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="h-8 text-xs"
                data-testid="custom-end-date"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              onClick={applyCustomRange}
              className="flex-1 h-7 text-xs"
              data-testid="apply-custom-range"
            >
              Apply
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => {
                setTimeRange('this_month');
                setShowCustomDatePicker(false);
              }}
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Date Range Info */}
      {dateRange.start && (
        <div className="mb-3 text-xs text-slate-500 flex items-center gap-1">
          <Calendar size={12} />
          <span>{getDateRangeLabel()}</span>
          {lastFetched && (
            <span className="text-slate-400 ml-auto">
              Updated {lastFetched.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      )}

      {/* Status Summary Pills */}
      {Object.keys(statusCounts).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {statusCounts.pending > 0 && (
            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs">
              {statusCounts.pending} Pending
            </span>
          )}
          {statusCounts.approved > 0 && (
            <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs">
              {statusCounts.approved} Approved
            </span>
          )}
          {statusCounts.agreement_done > 0 && (
            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs">
              {statusCounts.agreement_done} Agreement Done
            </span>
          )}
          {statusCounts.rejected > 0 && (
            <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded-full text-xs">
              {statusCounts.rejected} Rejected
            </span>
          )}
          {statusCounts.cancelled > 0 && (
            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs">
              {statusCounts.cancelled} Cancelled
            </span>
          )}
        </div>
      )}

      {/* Bookings List */}
      {loading ? (
        <div className="py-8 text-center">
          <RefreshCw size={20} className="animate-spin text-slate-400 mx-auto" />
          <p className="text-xs text-slate-400 mt-2">Loading bookings...</p>
        </div>
      ) : bookings.length === 0 ? (
        <div className="py-8 text-center">
          <Building2 size={32} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No bookings found</p>
          <p className="text-xs text-slate-400 mt-1">Try changing the time range or filter</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {/* Sort: Action Needed bookings first */}
          {[...bookings].sort((a, b) => {
            if (a.action_required && !b.action_required) return -1;
            if (!a.action_required && b.action_required) return 1;
            return 0;
          }).map((booking, idx) => (
            <div 
              key={booking.booking_id || idx}
              className={`p-3 rounded-xl border transition-all ${
                booking.action_required 
                  ? 'bg-red-50 border-red-200 ring-1 ring-red-300' 
                  : booking.display_status === 'Cancelled' 
                    ? 'bg-red-50/50 border-red-100' 
                    : 'bg-slate-50 border-slate-100'
              }`}
              data-testid={`booking-row-${idx}`}
            >
              {/* Query Alert Banner for Action Needed */}
              {booking.action_required && (
                <div className="mb-2 p-2 bg-red-100 rounded-lg border border-red-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-red-800">Query Raised by City Head</p>
                      {booking.query_remark && (
                        <p className="text-xs text-red-700 mt-0.5 line-clamp-2">{booking.query_remark}</p>
                      )}
                      {booking.query_raised_by_name && (
                        <p className="text-[10px] text-red-500 mt-0.5">
                          By {booking.query_raised_by_name} • {booking.query_raised_at ? new Date(booking.query_raised_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="mt-2 w-full bg-red-600 hover:bg-red-700 text-white h-7 text-xs"
                    onClick={() => openResubmitModal(booking)}
                    data-testid="edit-resubmit-btn"
                  >
                    <RefreshCw size={12} className="mr-1" />
                    Edit & Resubmit
                  </Button>
                </div>
              )}
              
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-800 truncate">{booking.project_name}</p>
                  <p className="text-xs text-slate-500 truncate">{booking.customer_name || 'Customer'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(booking)}
                  {/* Edit button - only for non-cancelled, non-query-raised bookings */}
                  {booking.display_status !== 'Cancelled' && !booking.action_required && (
                    <button
                      onClick={() => handleEditBooking(booking)}
                      className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="Request Edit"
                    >
                      <Edit3 size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xs text-slate-400">AV</p>
                    <p className="text-sm font-bold text-purple-700">{formatCurrency(booking.av_lacs)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Incentive</p>
                    {getIncentiveStatus(booking)}
                  </div>
                  {booking.agreement_status_display && booking.agreement_status_display !== 'Not Started' && (
                    <div>
                      <p className="text-xs text-slate-400">Agreement</p>
                      <p className="text-xs text-blue-600">{booking.agreement_status_display}</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-400">{booking.booking_date}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Live Update Indicator */}
      {pollingEnabled && (
        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-center gap-1">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-xs text-slate-400">Live updates every 45s</span>
        </div>
      )}

      {/* Edit Booking Request Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 size={18} className="text-purple-600" />
              Request Booking Edit
            </DialogTitle>
          </DialogHeader>
          
          {editBooking && (
            <div className="space-y-4">
              {/* Current Booking Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-800">{editBooking.project_name}</p>
                <p className="text-xs text-slate-500">{editBooking.customer_name}</p>
                <p className="text-xs text-slate-400 mt-1">Booking ID: {editBooking.booking_id}</p>
              </div>

              {/* Editable Fields */}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-medium text-slate-600">Booking Date</Label>
                  <Input
                    type="date"
                    value={editForm.booking_date}
                    onChange={(e) => setEditForm(prev => ({ ...prev, booking_date: e.target.value }))}
                    className="mt-1 h-10 rounded-xl"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div>
                  <Label className="text-xs font-medium text-slate-600">Unit No</Label>
                  <Input
                    value={editForm.unit_no}
                    onChange={(e) => setEditForm(prev => ({ ...prev, unit_no: e.target.value }))}
                    className="mt-1 h-10 rounded-xl"
                    placeholder="e.g., A-1205"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-600">AV (Lacs)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editForm.agreement_value_lacs}
                      onChange={(e) => setEditForm(prev => ({ ...prev, agreement_value_lacs: e.target.value }))}
                      className="mt-1 h-10 rounded-xl"
                      placeholder="126.37"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Token (Lacs)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editForm.token_amount_lacs}
                      onChange={(e) => setEditForm(prev => ({ ...prev, token_amount_lacs: e.target.value }))}
                      className="mt-1 h-10 rounded-xl"
                      placeholder="5.00"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-slate-600">Reason for Edit *</Label>
                  <Textarea
                    value={editForm.reason}
                    onChange={(e) => setEditForm(prev => ({ ...prev, reason: e.target.value }))}
                    className="mt-1 rounded-xl min-h-[80px]"
                    placeholder="Explain why you need to make these changes..."
                  />
                </div>
              </div>

              {/* Info Note */}
              <div className="flex gap-2 p-3 bg-amber-50 rounded-lg">
                <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Your edit request will be sent to your manager for approval. You'll be notified once it's processed.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowEditDialog(false)}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitEditRequest}
                  disabled={submittingEdit || !editForm.reason.trim()}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
                >
                  {submittingEdit ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit & Resubmit Modal (for QUERY_RAISED bookings) */}
      <Dialog open={showResubmitModal} onOpenChange={(open) => { if (!open) closeResubmitModal(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw size={18} className="text-red-600" />
              Submit Correction
            </DialogTitle>
          </DialogHeader>
          
          {resubmitBooking && (
            <div className="space-y-4">
              {/* Query Details - Structured Format */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-3">
                    <p className="text-sm font-bold text-red-800">⚠️ Query Raised by City Head</p>
                    
                    {/* Issue */}
                    <div>
                      <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wide">Issue:</p>
                      <p className="text-sm text-red-800 font-medium mt-0.5">
                        {resubmitBooking.query_remark || 'No details provided'}
                      </p>
                    </div>
                    
                    {/* Raised by & Raised on */}
                    <div className="grid grid-cols-2 gap-3">
                      {resubmitBooking.query_raised_by_name && (
                        <div>
                          <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wide">Raised by:</p>
                          <p className="text-xs text-red-700 mt-0.5">{resubmitBooking.query_raised_by_name}</p>
                        </div>
                      )}
                      {resubmitBooking.query_raised_at && (
                        <div>
                          <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wide">Raised on:</p>
                          <p className="text-xs text-red-700 mt-0.5">
                            {new Date(resubmitBooking.query_raised_at).toLocaleDateString('en-IN', { 
                              day: 'numeric', month: 'short' 
                            })} • {new Date(resubmitBooking.query_raised_at).toLocaleTimeString('en-IN', { 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking Info */}
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{resubmitBooking.project_name}</p>
                    <p className="text-xs text-slate-500">{resubmitBooking.customer_name}</p>
                  </div>
                  <p className="text-xs text-slate-400">ID: {resubmitBooking.booking_id}</p>
                </div>
              </div>

              {/* Correction Notes - MOVED UP */}
              <div>
                <Label className="text-xs font-medium text-slate-600">What did you fix? *</Label>
                <Textarea
                  value={resubmitForm.correction_notes}
                  onChange={(e) => setResubmitForm(prev => ({ ...prev, correction_notes: e.target.value }))}
                  className="mt-1 rounded-xl min-h-[70px]"
                  placeholder="Explain what you corrected to address the query..."
                />
              </div>

              {/* Editable Fields */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Update Fields (if needed)</p>
                
                <div>
                  <Label className="text-xs font-medium text-slate-600">Unit Number</Label>
                  <Input
                    value={resubmitForm.unit_no}
                    onChange={(e) => setResubmitForm(prev => ({ ...prev, unit_no: e.target.value }))}
                    className="mt-1 h-10 rounded-xl"
                    placeholder="e.g., A-1205"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-600">AV (₹ Lacs)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={resubmitForm.agreement_value_lacs}
                      onChange={(e) => setResubmitForm(prev => ({ ...prev, agreement_value_lacs: e.target.value }))}
                      className="mt-1 h-10 rounded-xl"
                      placeholder="126.37"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Token (₹ Lacs)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={resubmitForm.token_amount_lacs}
                      onChange={(e) => setResubmitForm(prev => ({ ...prev, token_amount_lacs: e.target.value }))}
                      className="mt-1 h-10 rounded-xl"
                      placeholder="5.00"
                    />
                  </div>
                </div>

              </div>

              {/* Multi-Document Upload Section */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Upload Documents</p>
                
                {/* Existing and New Documents List */}
                <div className="space-y-2">
                  {DOCUMENT_TYPES.filter(dt => bookingDocuments[dt.key]).map(dt => {
                    const doc = bookingDocuments[dt.key];
                    return (
                      <div 
                        key={dt.key} 
                        className={`flex items-center gap-2 p-2.5 rounded-xl border ${
                          doc.isNew 
                            ? 'bg-blue-50 border-blue-200' 
                            : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <FileText size={16} className={doc.isNew ? 'text-blue-600' : 'text-slate-500'} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700">{dt.label}</p>
                          <p className="text-[10px] text-slate-500 truncate">
                            {doc.isNew ? '📎 New upload' : doc.isExisting ? '✓ Previously uploaded' : ''}
                            {doc.uploaded_by_name && !doc.isNew && ` by ${doc.uploaded_by_name}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.url && (
                            <a 
                              href={doc.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View document"
                            >
                              <ChevronRight size={14} />
                            </a>
                          )}
                          {/* Replace button */}
                          <label className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer" title="Replace">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={(e) => handleDocumentUpload(e, dt.key)}
                              disabled={uploadingDocType === dt.key}
                            />
                            {uploadingDocType === dt.key ? (
                              <RefreshCw size={14} className="animate-spin" />
                            ) : (
                              <Edit3 size={14} />
                            )}
                          </label>
                          {/* Remove button */}
                          <button
                            onClick={() => handleRemoveDocument(dt.key)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add New Document Dropdown */}
                {DOCUMENT_TYPES.filter(dt => !bookingDocuments[dt.key]).length > 0 && (
                  <div className="relative">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full h-9 text-xs border-dashed"
                      onClick={() => setShowAddDocDropdown(!showAddDocDropdown)}
                    >
                      + Add Document
                    </Button>
                    
                    {showAddDocDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-10 overflow-hidden">
                        {DOCUMENT_TYPES.filter(dt => !bookingDocuments[dt.key]).map(dt => (
                          <label 
                            key={dt.key}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 last:border-0"
                          >
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={(e) => {
                                handleDocumentUpload(e, dt.key);
                                setShowAddDocDropdown(false);
                              }}
                              disabled={uploadingDocType === dt.key}
                            />
                            <FileText size={14} className="text-slate-400" />
                            <span className="text-xs text-slate-700">{dt.label}</span>
                            {uploadingDocType === dt.key && (
                              <RefreshCw size={12} className="ml-auto text-slate-400 animate-spin" />
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Upload hint */}
                <p className="text-[10px] text-slate-400 text-center">
                  Supported: PDF, JPG, PNG (max 10MB each)
                </p>
              </div>

              {/* Changes Preview */}
              {(() => {
                const changes = [];
                const origUnit = resubmitBooking.unit_no || '';
                const origAV = resubmitBooking.av_lacs?.toString() || '';
                const origToken = resubmitBooking.token_lacs?.toString() || '';
                
                if (resubmitForm.unit_no !== origUnit) {
                  changes.push({ field: 'Unit Number', from: origUnit || '(empty)', to: resubmitForm.unit_no || '(empty)' });
                }
                if (resubmitForm.agreement_value_lacs !== origAV) {
                  changes.push({ field: 'AV', from: origAV ? `₹${origAV}L` : '(empty)', to: resubmitForm.agreement_value_lacs ? `₹${resubmitForm.agreement_value_lacs}L` : '(empty)' });
                }
                if (resubmitForm.token_amount_lacs !== origToken) {
                  changes.push({ field: 'Token', from: origToken ? `₹${origToken}L` : '(empty)', to: resubmitForm.token_amount_lacs ? `₹${resubmitForm.token_amount_lacs}L` : '(empty)' });
                }
                
                // Check for new document uploads
                const newDocs = Object.entries(bookingDocuments)
                  .filter(([_, doc]) => doc.isNew)
                  .map(([docType]) => DOCUMENT_TYPES.find(dt => dt.key === docType)?.label || docType);
                
                if (newDocs.length > 0) {
                  changes.push({ field: 'Documents', from: '—', to: `+${newDocs.length} uploaded (${newDocs.join(', ')})` });
                }
                
                if (changes.length > 0) {
                  return (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                      <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide mb-2">Changes Made:</p>
                      <div className="space-y-1">
                        {changes.map((change, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs flex-wrap">
                            <span className="font-medium text-blue-800">{change.field}:</span>
                            <span className="text-blue-600 line-through">{change.from}</span>
                            <span className="text-blue-700">→</span>
                            <span className="text-blue-800 font-semibold">{change.to}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={closeResubmitModal}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleResubmitBooking}
                  disabled={submittingResubmit || !resubmitForm.correction_notes.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl"
                >
                  {submittingResubmit ? (
                    <>
                      <RefreshCw size={14} className="mr-1 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={14} className="mr-1" />
                      Submit Correction
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const RepPerformance = ({ user }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [actions, setActions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchSummary(selectedMonth);
    }
  }, [selectedMonth]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [monthsRes, actionsRes] = await Promise.all([
        api.getAVAvailableMonths(),
        api.getTodayActions()
      ]);
      
      const months = monthsRes.data?.months || [];
      setAvailableMonths(months);
      setActions(actionsRes.data);
      
      // Set current month as default
      const currentMonth = new Date().toISOString().slice(0, 7);
      setSelectedMonth(months.includes(currentMonth) ? currentMonth : months[0] || currentMonth);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async (month) => {
    try {
      const response = await api.getAVMonthlySummary(month);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchSummary(selectedMonth),
        api.getTodayActions().then(res => setActions(res.data))
      ]);
      hapticSuccess();
      toast.success('Data refreshed');
    } catch (error) {
      toast.error('Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const formatMonthLabel = (monthStr) => {
    if (!monthStr) return '';
    try {
      const [year, month] = monthStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
    } catch {
      return monthStr;
    }
  };

  // Calculate total urgent actions
  const totalUrgent = useMemo(() => {
    return (actions?.overdue_followups_count || 0) + 
           (actions?.today_followups_count || 0) + 
           (actions?.today_visits_count || 0);
  }, [actions]);

  if (loading || !summary) {
    return <PerformanceSkeleton />;
  }

  const { approved, confirmed, slabs } = summary;
  const isUnderMinimum = approved.av_cr < 1;

  return (
    <div className="flex flex-col h-[100dvh] bg-[#F7F8FA] page-fade-in">
      {/* Header - Fixed at top */}
      <div className="flex-shrink-0 bg-gradient-to-br from-[#5B3DF5] to-[#4A2FE0] text-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold">My Performance</h1>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <MobileMenuTrigger user={user} className="text-white hover:bg-white/10" />
          </div>
        </div>
        
        {/* Month Selector */}
        <div className="mb-4">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-full bg-white/10 border-white/20 text-white h-9" data-testid="month-selector">
              <Calendar size={14} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableMonths.map(m => (
                <SelectItem key={m} value={m}>{formatMonthLabel(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Monthly AV Summary (Top Block) */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div>
              <p className="text-xs text-white/60 mb-1">Monthly AV (MTD)</p>
              <p className="text-2xl font-black">{formatCurrency(approved.av_lacs)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-white/60 mb-1">Current Slab</p>
              <p className="text-sm font-bold bg-white/20 rounded-lg py-1 px-2">
                {approved.current_slab?.label || '₹0 - ₹99L'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-white/60 mb-1">Notional Incentive</p>
              <p className="text-2xl font-black text-yellow-300">{formatINR(approved.incentive)}</p>
            </div>
          </div>
          <p className="text-xs text-white/50 text-center">
            Final payout calculated after agreement completion.
          </p>
        </div>
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 space-y-4 pb-24 scrollbar-hide">
        
        {/* Slab Progress Meter */}
        <Card className="p-4 bg-white rounded-2xl shadow-sm border-0">
          <div className="flex items-center gap-2 mb-2">
            <Award size={16} className="text-purple-600" />
            <h3 className="text-sm font-bold text-slate-800">Slab Progress</h3>
          </div>
          <SlabProgressMeter currentAVCr={approved.av_cr} slabs={slabs} />
        </Card>
        
        {/* Next Slab Prompt */}
        {isUnderMinimum ? (
          <Card className="p-4 bg-amber-50 border border-amber-200 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-800">Minimum ₹1 Cr AV required</p>
                <p className="text-xs text-amber-600">
                  You need ₹{((1 - approved.av_cr) * 100).toFixed(0)}L more to unlock your first incentive slab.
                </p>
              </div>
            </div>
          </Card>
        ) : approved.gap_lacs && approved.next_slab_payout ? (
          <Card className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Target size={20} className="text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-purple-800">
                  {approved.av_cr >= 3 
                    ? `Next ₹50L unlocks additional ₹10,000`
                    : `₹${Math.ceil(approved.gap_lacs)}L more to unlock ${formatINR(approved.next_slab_payout)} slab`
                  }
                </p>
                <p className="text-xs text-purple-600">
                  {approved.av_cr >= 3 
                    ? `You're in the top tier! Every ₹50L adds ₹10,000.`
                    : `Current: ${formatINR(approved.incentive)} → Next: ${formatINR(approved.next_slab_payout)}`
                  }
                </p>
              </div>
              <ChevronRight size={20} className="text-purple-400" />
            </div>
          </Card>
        ) : null}

        {/* Confirmed vs Notional */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4 bg-white rounded-2xl shadow-sm border-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle size={16} className="text-blue-600" />
              </div>
              <span className="text-xs text-slate-500 font-medium">Notional</span>
            </div>
            <p className="text-xl font-bold text-slate-800">{formatINR(approved.incentive)}</p>
            <p className="text-xs text-slate-400 mt-1">{approved.count} approved</p>
          </Card>
          
          <Card className="p-4 bg-white rounded-2xl shadow-sm border-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Award size={16} className="text-emerald-600" />
              </div>
              <span className="text-xs text-slate-500 font-medium">Confirmed</span>
            </div>
            <p className="text-xl font-bold text-emerald-600">{formatINR(confirmed.incentive)}</p>
            <p className="text-xs text-slate-400 mt-1">{confirmed.count} agreements done</p>
          </Card>
        </div>
        
        {/* Actions for Today */}
        {totalUrgent > 0 && (
          <Card className="p-4 bg-white rounded-2xl shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Flame size={16} className="text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">Actions for Today</h3>
                  <p className="text-xs text-slate-400">{totalUrgent} items need attention</p>
                </div>
              </div>
              <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-full">
                {totalUrgent}
              </span>
            </div>
            
            <div className="space-y-2">
              {actions?.overdue_followups_count > 0 && (
                <button
                  onClick={() => navigate('/crm?filter=followups_due')}
                  className="w-full flex items-center justify-between p-3 bg-red-50 rounded-xl hover:bg-red-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle size={18} className="text-red-600" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-red-700">Overdue Follow-ups</p>
                      <p className="text-xs text-red-500">{actions.overdue_followups_count} need immediate action</p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-red-400" />
                </button>
              )}
              
              {actions?.today_followups_count > 0 && (
                <button
                  onClick={() => navigate('/crm?filter=followups_due')}
                  className="w-full flex items-center justify-between p-3 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-amber-600" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-amber-700">Follow-ups Due Today</p>
                      <p className="text-xs text-amber-500">{actions.today_followups_count} scheduled</p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-amber-400" />
                </button>
              )}
              
              {actions?.today_visits_count > 0 && (
                <button
                  onClick={() => navigate('/crm')}
                  className="w-full flex items-center justify-between p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Calendar size={18} className="text-blue-600" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-blue-700">Scheduled Visits</p>
                      <p className="text-xs text-blue-500">{actions.today_visits_count} today</p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-blue-400" />
                </button>
              )}
            </div>
          </Card>
        )}
        
        {/* Booking Contribution Table - Self-contained with time range and live updates */}
        <BookingContributionTable pollingEnabled={true} />
        
        {/* Info Card */}
        <Card className="p-4 bg-slate-50 border border-slate-200 rounded-2xl">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-slate-400 mt-0.5" />
            <div className="text-xs text-slate-500 space-y-1">
              <p><strong>Notional:</strong> Based on Approved AV (may change if cancelled)</p>
              <p><strong>Confirmed:</strong> Based on Agreement Completed AV (locked)</p>
              <p><strong>Incentive resets</strong> on the 1st of every month</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RepPerformance;
